import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import {
  getAdminList, createAdminAccount, deleteAdminAccount,
  updateAdminPermissions, getSupportRoleDefaults, updateSupportRoleDefaults
} from "../../api/client";
import { useAuth } from "../../context/AuthContext";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const PERMISSION_LABELS = {
  riders: "Fleet (Riders & Vehicles)",
  orders: "Orders Management",
  revenue: "Revenue Dashboard & Reports",
  users: "Users & Accounts",
  agents: "Agents & Referrals",
  fleetOwners: "Fleet Owners",
  invoices: "B2B Corporate Invoices",
  pricing: "Pricing & Split Settings",
  popups: "Screen Pop-up Banners",
  dispatch: "Live Dispatch Board"
};

const ROLE_DEFINITIONS = [
  { value: "super_admin", label: "👑 Super Admin — full platform governance & admin management" },
  { value: "admin", label: "⚙️ Admin — full operational, pricing & configuration access" },
  { value: "dispatch_manager", label: "🛵 Dispatch Manager — riders, orders, fleet & live dispatch" },
  { value: "finance_admin", label: "💰 Finance Admin — revenue, invoices, settlements & COD" },
  { value: "operations_manager", label: "📋 Operations Manager — fleet, users, popups & agents" },
  { value: "support", label: "🎧 Support — customer resolution, orders & user tickets" }
];

const DEFAULT_ROLE_PERMS = {
  super_admin: ["riders", "orders", "revenue", "users", "agents", "fleetOwners", "invoices", "pricing", "popups", "dispatch"],
  admin: ["riders", "orders", "revenue", "users", "agents", "fleetOwners", "invoices", "pricing", "popups", "dispatch"],
  dispatch_manager: ["riders", "orders", "fleetOwners", "dispatch"],
  finance_admin: ["revenue", "invoices", "orders"],
  operations_manager: ["riders", "users", "agents", "fleetOwners", "popups"],
  support: ["orders", "users"]
};

const ROLE_BADGES = {
  super_admin: { label: "Super Admin", color: "#a855f7", bg: "rgba(168, 85, 247, 0.15)" },
  admin: { label: "Administrator", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.15)" },
  dispatch_manager: { label: "Dispatch Manager", color: "#10b981", bg: "rgba(16, 185, 129, 0.15)" },
  finance_admin: { label: "Finance Admin", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.15)" },
  operations_manager: { label: "Operations Manager", color: "#06b6d4", bg: "rgba(6, 182, 212, 0.15)" },
  support: { label: "Support", color: "#64748b", bg: "rgba(100, 116, 139, 0.15)" }
};

function PermissionChecklist({ allPermissions, selected, onChange, disabled }) {
  const perms = allPermissions?.length ? allPermissions : Object.keys(PERMISSION_LABELS);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px" }}>
      {perms.map((key) => (
        <label key={key} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 400 }}>
          <input
            type="checkbox"
            checked={selected.includes(key)}
            disabled={disabled}
            onChange={(e) => {
              onChange(e.target.checked ? [...selected, key] : selected.filter((p) => p !== key));
            }}
          />
          {PERMISSION_LABELS[key] || key}
        </label>
      ))}
    </div>
  );
}

export default function AdminAdmins() {
  const { account } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    permissions: DEFAULT_ROLE_PERMS.admin
  });
  const [formError, setFormError] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const [allPermissions, setAllPermissions] = useState(Object.keys(PERMISSION_LABELS));
  const [defaults, setDefaults] = useState(DEFAULT_ROLE_PERMS.support);
  const [defaultsBusy, setDefaultsBusy] = useState(false);
  const [defaultsSaved, setDefaultsSaved] = useState(false);

  // Per-admin permission editing
  const [editingId, setEditingId] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [editBusy, setEditBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getAdminList();
      setAdmins(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load admin accounts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDefaults = async () => {
    try {
      const { data } = await getSupportRoleDefaults();
      if (data.allPermissions?.length) setAllPermissions(data.allPermissions);
      if (data.permissions?.length) setDefaults(data.permissions);
    } catch (err) {
      // Non-fatal fallback
    }
  };

  useEffect(() => { load(); loadDefaults(); }, []);

  const handleRoleChange = (role) => {
    const rolePerms = role === "support" ? defaults : (DEFAULT_ROLE_PERMS[role] || []);
    setForm((f) => ({ ...f, role, permissions: rolePerms }));
  };

  const handleSaveDefaults = async () => {
    setDefaultsBusy(true);
    setDefaultsSaved(false);
    setError("");
    try {
      const { data } = await updateSupportRoleDefaults(defaults);
      setDefaults(data.permissions);
      setDefaultsSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save support role defaults.");
    } finally {
      setDefaultsBusy(false);
    }
  };

  const startEditingPermissions = (admin) => {
    setEditingId(admin._id);
    setEditingPermissions(admin.permissions || DEFAULT_ROLE_PERMS[admin.role] || []);
  };

  const handleSavePermissions = async (adminId) => {
    setEditBusy(true);
    setError("");
    try {
      await updateAdminPermissions(adminId, editingPermissions);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not save permissions.");
    } finally {
      setEditBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name || !form.email || !form.password) {
      setFormError("All fields are required.");
      return;
    }
    if (form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    setCreating(true);
    try {
      await createAdminAccount({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        permissions: form.permissions
      });
      setForm({ name: "", email: "", password: "", role: "admin", permissions: DEFAULT_ROLE_PERMS.admin });
      setShowCreate(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || "Could not create admin account.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Are you sure you want to remove ${admin.name} (${admin.email})?`)) return;
    setBusyId(admin._id);
    try {
      await deleteAdminAccount(admin._id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not delete admin account.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h1 style={{ margin: "0 0 4px" }}>👥 Admin & Staff Accounts</h1>
            <p className="hint" style={{ margin: 0 }}>Manage team roles, access levels, and granular feature permissions.</p>
          </div>
          <button
            style={{ width: "auto", padding: "10px 20px" }}
            onClick={() => { setShowCreate(!showCreate); setFormError(""); }}
          >
            {showCreate ? "Cancel" : "+ New Staff Account"}
          </button>
        </div>

        {/* CREATE ADMIN FORM */}
        {showCreate && (
          <div className="delivery-card" style={{ marginBottom: "24px", border: "1px solid var(--primary)" }}>
            <h3 style={{ margin: "0 0 16px" }}>➕ Create New Staff Account</h3>
            {formError && <p className="error" style={{ marginBottom: "12px" }}>{formError}</p>}
            <form onSubmit={handleCreate}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Full Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Tunde Balogun"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    style={{ margin: 0 }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Email Address</label>
                  <input
                    type="email"
                    placeholder="staff@deliverng.com"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    style={{ margin: 0 }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginTop: "12px" }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Temporary Password</label>
                <input
                  type="password"
                  placeholder="Minimum 8 characters..."
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  style={{ margin: 0, width: "100%" }}
                  required
                  minLength={8}
                />
              </div>

              <div style={{ marginTop: "12px" }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Staff Role Category</label>
                <select
                  value={form.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  style={{ margin: 0 }}
                >
                  {ROLE_DEFINITIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>
                    Granted Permissions ({form.permissions.length} active)
                  </label>
                  <button
                    type="button"
                    className="secondary"
                    style={{ width: "auto", padding: "4px 8px", fontSize: "11px", margin: 0 }}
                    onClick={() => setForm((f) => ({ ...f, permissions: DEFAULT_ROLE_PERMS[f.role] || [] }))}
                  >
                    Reset to Role Defaults
                  </button>
                </div>
                <PermissionChecklist
                  allPermissions={allPermissions}
                  selected={form.permissions}
                  onChange={(permissions) => setForm((f) => ({ ...f, permissions }))}
                />
              </div>

              <button type="submit" disabled={creating} style={{ marginTop: "16px", width: "auto", padding: "10px 24px" }}>
                {creating ? "Creating..." : "✅ Create Staff Account"}
              </button>
            </form>
          </div>
        )}

        {/* Support Role Defaults Card */}
        <div className="delivery-card" style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "4px" }}>🧩 Support Role Baseline Defaults</h3>
          <p className="hint">The baseline permission set granted to newly created Support accounts.</p>
          <PermissionChecklist
            allPermissions={allPermissions}
            selected={defaults}
            onChange={setDefaults}
          />
          <button
            className="secondary"
            style={{ width: "auto", padding: "8px 16px", marginTop: "12px" }}
            disabled={defaultsBusy}
            onClick={handleSaveDefaults}
          >
            {defaultsBusy ? "Saving..." : defaultsSaved ? "✓ Saved Defaults" : "Save Support Defaults"}
          </button>
        </div>

        {error && <p className="error">{error}</p>}

        {loading && (
          <div className="empty-state">
            <span className="empty-state-icon">📡</span>
            <p className="empty-state-text waiting-dots">Loading staff accounts</p>
          </div>
        )}

        {!loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {admins.map((admin) => {
              const isSelf = account?.id === admin._id;
              const roleMeta = ROLE_BADGES[admin.role] || ROLE_BADGES.support;
              const isSuper = admin.role === "super_admin";

              return (
                <div key={admin._id} className="delivery-card" style={{ padding: "16px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{
                        width: "42px", height: "42px", borderRadius: "50%",
                        background: roleMeta.bg,
                        border: `1px solid ${roleMeta.color}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 800, fontSize: "16px", color: roleMeta.color
                      }}>
                        {admin.name?.charAt(0)?.toUpperCase() || "A"}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                          {admin.name}
                          {isSelf && (
                            <span style={{ fontSize: "11px", background: "rgba(16, 185, 129, 0.2)", color: "#10b981", padding: "2px 8px", borderRadius: "12px" }}>
                              You
                            </span>
                          )}
                          <span style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: roleMeta.bg,
                            color: roleMeta.color,
                            fontWeight: 600
                          }}>
                            {roleMeta.label}
                          </span>
                        </div>
                        <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{admin.email}</div>
                        <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                          Member since {formatDate(admin.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                      {!isSuper && editingId !== admin._id && (
                        <button
                          className="secondary"
                          style={{ width: "auto", padding: "6px 12px", fontSize: "12px", margin: 0 }}
                          onClick={() => startEditingPermissions(admin)}
                        >
                          ✏️ Edit Permissions
                        </button>
                      )}
                      {!isSelf && (
                        <button
                          className="secondary"
                          style={{ width: "auto", padding: "6px 12px", fontSize: "12px", margin: 0, borderColor: "var(--danger)", color: "var(--danger)" }}
                          disabled={busyId === admin._id}
                          onClick={() => handleDelete(admin)}
                        >
                          {busyId === admin._id ? "Removing..." : "🗑️ Remove"}
                        </button>
                      )}
                    </div>
                  </div>

                  {editingId !== admin._id && (
                    <div style={{ marginTop: "10px", fontSize: "12px", color: "var(--text-muted)", display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Permissions:</span>
                      {admin.permissions?.length ? (
                        admin.permissions.map((p) => (
                          <span key={p} style={{ background: "rgba(255,255,255,0.06)", padding: "1px 6px", borderRadius: "4px" }}>
                            {PERMISSION_LABELS[p] || p}
                          </span>
                        ))
                      ) : (
                        <span>Default role permissions</span>
                      )}
                    </div>
                  )}

                  {editingId === admin._id && (
                    <div style={{ marginTop: "14px", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <span style={{ fontSize: "12px", fontWeight: 600 }}>Edit Permissions for {admin.name}</span>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            type="button"
                            className="secondary"
                            style={{ width: "auto", padding: "3px 8px", fontSize: "11px", margin: 0 }}
                            onClick={() => setEditingPermissions(DEFAULT_ROLE_PERMS[admin.role] || [])}
                          >
                            Reset to {admin.role} Defaults
                          </button>
                        </div>
                      </div>
                      <PermissionChecklist
                        allPermissions={allPermissions}
                        selected={editingPermissions}
                        onChange={setEditingPermissions}
                      />
                      <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                        <button
                          style={{ width: "auto", padding: "6px 16px", margin: 0 }}
                          disabled={editBusy}
                          onClick={() => handleSavePermissions(admin._id)}
                        >
                          {editBusy ? "Saving..." : "Save Permissions"}
                        </button>
                        <button
                          className="secondary"
                          style={{ width: "auto", padding: "6px 16px", margin: 0 }}
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
